/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles, Tooltip} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity} from '@ion/core/src/types/entities';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {
  dlsAlt,
  dlsColor,
  dlsZIndex
} from '@ion/dls/src/theme/theme';
import {
  getOptionLabel,
  getOptionSelected
} from '@ion/features/src/helpers';

// defines the identifier types chooser prop definitions ...
export type IdentifierTypeChooserProps = {
  onChange?: (option: Options) => void;
  defaultValue?: string;
  identifiers: string[];
  entity: BulkActionEntity;
  readOnly?: boolean;
  required: boolean;
  hasError?: boolean;
}

// defines the styles used by types chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : dlsZIndex('deviceTypeChooserPopper')
  },
  paper : {
    padding : '0px !important'
  },
  info : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    cursor         : 'pointer'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  formControl : {
    borderBottom           : ({hasError}: {hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: {hasError: boolean;}) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : 'none !important'
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 * Returns a list of supported CTC Identifier types suitable for a dropdown list.
 */
const getIdentifierTypes = (identifiers): Options[] => {
  return identifiers?.map(({label, value}) => ({label, value})) as Options[] || [];
};

/**
 * Provides a utility which filters a list of Identifier Types.
 */
const getSelectedValue = (
  options: Options[],
  selected: string
): Options => {
  return options?.find((option: any) => option.label === selected);
};

/**
 * The IdentifierTypeChooser provides a list of Identifier types.
 *
 * ```typescript
 *
 *     import {IdentifierTypeChooser} from '@ion/applications/src/bulk';
 *
 *     // read-only mode ...
 *     <IdentifierTypeChooser readOnly />
 *
 *     // with default value and required check ...
 *     <IdentifierTypeChooser required />
 *
 *     // with callback subscription ...
 *     <IdentifierTypeChooser onChange={setIdentifierTypes}
 *       />
 *
 * ```
 *
 *
 * @onChange                         Callback prop which will be invoked with the selected PEG Behavior
 * @readOnly                         When true, the component will be disabled/displayed in read-only mode;
 *                                   defaults to false
 * @required                         When true, the required indicator will be displayed
 * @validationError                  When true, the validationError indicator will be displayed
 * @classes                          Optional classes to apply to the wrapping element
 * @defaultValue                     Default value of the field
 */
export const IdentifierTypeChooser = ({
  onChange,
  defaultValue,
  identifiers,
  entity,
  readOnly,
  required,
  hasError = false
}: IdentifierTypeChooserProps) => {
  const _classes                = useStyles({hasError});
  const [open, setOpen]         = useState<boolean>(false);
  const [selected, setSelected] = useState<string>(null);
  const [options, setOptions]   = useState<Options[]>([]);
  const hasMounted              = useHasMounted();

  const entityLabel = translate('common:entityLabels', {entity, returnObjects: true});
  const {choosers}  = translate('common:editors', {entity: entityLabel[entity], returnObjects: true});

  useEffect(() => {
    if (!hasMounted) {
      const options = getIdentifierTypes(identifiers);
      setOptions(options);
      if (defaultValue) {
        onChange(getSelectedValue(options, defaultValue));
      } else if (selected) {
        onChange?.(undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  const onIdTypeSelection = useCallback(function(identifierType: string) {
    if (identifierType === 'ID') {
      return (
        <Tooltip arrow title={translate('common:editors.toolTip.info')}>
          <span>
            <InfoIcon />
          </span>
        </Tooltip>
      );
    }
  }, []);

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    if (e) {
      e.persist();
    }
  };

  return (
    <div className={_classes.root}>
      <FormControl className={_classes.formControl}>
        <Autocomplete disabled={readOnly}
          defaultValue={defaultValue || ''}
          autoComplete={false}
          clearOnBlur={false}
          openText=''
          closeText=''
          clearText={`${choosers.identifierTypes.clearText}`}
          classes={{
            popper         : _classes.popper,
            paper          : _classes.paper,
            popupIndicator : _classes.popupIndicator,
            clearIndicator : _classes.clearIndicator,
            endAdornment   : _classes.endAdornment,
            input          : _classes.input,
            inputRoot      : _classes.inputRoot,
            listbox        : _classes.listbox,
            option         : _classes.option
          }}
          open={open}
          onOpen={function() {
            setOpen(true);
          }}
          onClose={function() {
            setOpen(false);
          }}
          getOptionSelected={getOptionSelected}
          getOptionLabel={getOptionLabel}
          onChange={onChange ? function(event, value: Options) {
            const val = value?.label;
            setSelected(val);
            onChange(value ? getSelectedValue(options, val) : null);
          } : null}
          options={options}
          loading={false}
          selectOnFocus={false}
          clearOnEscape
          noOptionsText={`${choosers.identifierTypes.noOptionsText}`}
          onInputChange={handleFieldChange}
          renderInput={function(params) {
            return (
              <FormTextField {...params}
                autoComplete='off'
                error={hasError}
                helperText={hasError && required ? translate('common:editors.shared.required') : ''}
                required={required}
                fieldLabel={`${choosers.identifierTypes.label}`}
                placeholder={`${choosers.identifierTypes.placeholder}`}
                name={`${choosers.identifierTypes[entity].name}`}
                InputProps={{
                  ...params.InputProps,
                  endAdornment : (<div className={_classes.info}>
                    {onIdTypeSelection(selected)}
                  </div>)
                }} />
            );
          }} />
      </FormControl>
    </div>
  );
};

export default memo(IdentifierTypeChooser);
